import { useEffect, useRef, useState } from "react";
import { MessageSquarePlus } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const SCALE = [1, 2, 3, 4, 5];

function ScaleControl({ value, guide, disabled, onSelect, testPrefix }) {
  return (
    <div>
      <div role="radiogroup" aria-label="Puan" className="flex items-center gap-2 sm:gap-3">
        {SCALE.map((n) => (
          <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} — ${guide?.[n] || ""}`} title={guide?.[n]} disabled={disabled} data-selected={value === n} data-testid={`${testPrefix}-score-${n}`} onClick={() => onSelect(n)} className="score-dot">{n}</button>
        ))}
      </div>
      <div className="mt-2 flex w-[calc(5*44px+4*12px)] max-w-full justify-between text-xs text-plena-muted"><span>Zayıf</span><span>Çok Güçlü</span></div>
      <p className="mt-1.5 min-h-[1.25rem] text-sm text-plena-text2" data-testid={`${testPrefix}-score-label`}>{value ? guide?.[value] : ""}</p>
    </div>
  );
}

function BinaryControl({ value, disabled, onSelect, testPrefix }) {
  return (
    <div className="flex gap-2" role="radiogroup">
      {[[1, "Evet"], [0, "Hayır"]].map(([v, l]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} disabled={disabled} data-testid={`${testPrefix}-binary-${v}`} onClick={() => onSelect(v)} className={cn("btn-press rounded-full border px-5 py-2 text-sm font-medium transition-colors", value === v ? "border-plena-text bg-plena-text text-white" : "border-plena-border bg-white hover:border-[#C9CDD6]")}>{l}</button>
      ))}
    </div>
  );
}

export function CriterionCard({ criterion, value, disabled, onChange }) {
  const current = value || { score: null, evidence_status: "SUFFICIENT", comment: null };
  const [noteOpen, setNoteOpen] = useState(!!current.comment);
  const [note, setNote] = useState(current.comment || "");
  const [num, setNum] = useState(current.score ?? "");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const insufficient = current.evidence_status === "INSUFFICIENT";
  const prefix = `criterion-${criterion.id}`;

  const commit = (patch) => onChange({ ...current, comment: note || null, ...patch }).catch(() => {});
  const setScore = (s) => commit({ score: s, evidence_status: "SUFFICIENT" });
  const toggleInsufficient = () => commit(insufficient ? { score: null, evidence_status: "SUFFICIENT" } : { score: null, evidence_status: "INSUFFICIENT" });
  const onNote = (v) => { setNote(v); clearTimeout(timer.current); timer.current = setTimeout(() => onChange({ ...current, comment: v || null }).catch(() => {}), 600); };
  const onNum = (v) => { setNum(v); clearTimeout(timer.current); timer.current = setTimeout(() => v !== "" && commit({ score: Number(v) }), 500); };

  return (
    <div data-testid={`criterion-card-${criterion.id}`} className="py-7 first:pt-2 last:pb-2">
      <div className="mb-4 max-w-xl">
        <p className="font-heading text-base font-semibold">{criterion.name}</p>
        {criterion.description && <p className="mt-1 text-sm leading-relaxed text-plena-text2">{criterion.description}</p>}
        {criterion.config?.job_note && <p className="mt-2 rounded-xl bg-plena-surface2 px-3.5 py-2 text-sm leading-relaxed text-plena-text" data-testid={`${prefix}-job-note`}>{criterion.config.job_note}</p>}
      </div>
      <div className={cn("transition-opacity duration-200", insufficient && "opacity-40")}>
        {criterion.evaluation_type === "BINARY" && <BinaryControl value={current.score} disabled={disabled || insufficient} onSelect={setScore} testPrefix={prefix} />}
        {criterion.evaluation_type === "NUMERIC_THRESHOLD" && <Input type="number" data-testid={`${prefix}-numeric`} value={num} disabled={disabled || insufficient} onChange={(e) => onNum(e.target.value)} className="h-11 w-40" placeholder={criterion.config?.unit ? `Değer (${criterion.config.unit})` : "Değer"} />}
        {(criterion.evaluation_type === "SCALE" || criterion.evaluation_type === "QUALITATIVE") && <ScaleControl value={current.score} guide={criterion.scoring_guide} disabled={disabled || insufficient} onSelect={setScore} testPrefix={prefix} />}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button type="button" disabled={disabled} aria-pressed={insufficient} data-testid={`${prefix}-insufficient`} onClick={toggleInsufficient} className={cn("btn-press rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors", insufficient ? "border-[#E3C56A] bg-[#FDF4DC] text-[#8A6414]" : "border-plena-border bg-white text-plena-text2 hover:border-[#C9CDD6] hover:text-plena-text")}>Yeterli bilgi yok</button>
        {!noteOpen && <button type="button" disabled={disabled} data-testid={`${prefix}-add-note`} onClick={() => setNoteOpen(true)} className="inline-flex items-center gap-1 rounded-full px-2 py-1.5 text-xs font-medium text-plena-text2 hover:text-plena-text"><MessageSquarePlus className="h-3.5 w-3.5" /> Not ekle</button>}
      </div>
      {insufficient && <p className="mt-2 text-xs text-plena-text2">CV'de bu kriteri değerlendirmek için yeterli bilgi bulunmuyor olarak işaretlendi.</p>}
      {noteOpen && <Textarea data-testid={`${prefix}-note`} value={note} disabled={disabled} onChange={(e) => onNote(e.target.value)} rows={2} placeholder="İsteğe bağlı not…" className="mt-3 max-w-xl rounded-xl" />}
    </div>
  );
}
